import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed — try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col" data-testid="forgot-password-page">
      <nav className="border-b hairline">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center"><Sparkle size={18} weight="duotone" /></div>
            <span className="font-serif-display text-xl">My Silo</span>
          </Link>
          <Link to="/login" data-testid="forgot-back-to-login" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      </nav>

      <main className="siloart-copy flex-1 flex items-center justify-center px-6 py-12">
        {sent ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8 text-center" data-testid="forgot-sent-confirm">
            <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mx-auto">
              <EnvelopeSimple size={22} weight="duotone" />
            </div>
            <h1 className="font-serif-display text-3xl text-[#1C1B1A] mt-4">Check your email.</h1>
            <p className="text-[#6B6A68] text-sm mt-2 leading-relaxed">
              If an account exists for <strong className="text-[#1C1B1A]">{email}</strong>, we've sent a reset link. The link is valid for 30 minutes and can only be used once.
            </p>
            <p className="text-xs text-[#6B6A68] mt-4">
              Didn't get it? Check your spam folder, or <button onClick={() => setSent(false)} className="text-[#4A5D4E] underline" data-testid="forgot-try-another">try another email</button>.
            </p>
          </motion.div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
            className="w-full max-w-md bg-white border hairline rounded-xl shadow-sm p-8">
            <h1 className="font-serif-display text-3xl text-[#1C1B1A]">Forgot your password?</h1>
            <p className="text-[#6B6A68] text-sm mt-1">Enter your account email and we'll send you a link to reset it.</p>

            <label className="block text-sm text-[#6B6A68] mt-6 mb-1.5">Email</label>
            <input
              data-testid="forgot-email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border hairline rounded-md px-4 py-3 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] focus:border-[#4A5D4E]"
              placeholder="you@email.com"
            />

            <button type="submit" disabled={loading || !email.trim()} data-testid="forgot-submit"
              className="mt-6 w-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] px-6 py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              <EnvelopeSimple size={16} weight="duotone" /> {loading ? "Sending…" : "Send reset link"}
            </button>
            <p className="text-xs text-[#6B6A68] mt-4 text-center">
              We'll never reveal whether an email is registered. If your address exists, you'll get the link.
            </p>
          </motion.form>
        )}
      </main>
    </div>
  );
}
